import { Loyalty } from '@getjust/api-schema';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAtom } from 'jotai';

import { loyaltyDiscountCodeAtom, loyaltyRedeemedAtom } from '$business/atoms/loyalty';
import { AnalyticsEventName, useTrackAnalytics } from '$src/hooks/client/useTrackAnalytics';
import { useAddDiscount } from '$src/hooks/mutations/useAddDiscount';
import { useFormMessageAtom } from '$src/hooks/state';
import { gateway } from '$src/http';

const MUTATION_LOYALTY_REDEEM_KEY = 'MUTATION/LOYALTY_REDEEM';
const LOYALTY_BALANCE_QUERY_KEY = 'QUERY/LOYALTY_BALANCE';

// Hey Pongo returns the coupon code a moment before the matching Shopify discount becomes
// queryable on the storefront, so the first apply can race ahead of it. Retry a few times
// with a short delay to let the discount propagate before giving up.
const DISCOUNT_APPLY_MAX_ATTEMPTS = 5;
const DISCOUNT_APPLY_RETRY_DELAY_MS = 1500;

type RedeemInput = Loyalty.LoyaltyCreateRedemptionRequestBody;

export const useLoyaltyRedeem = () => {
  const { formMessage } = useFormMessageAtom();
  const queryClient = useQueryClient();
  const [redeemed, setRedeemed] = useAtom(loyaltyRedeemedAtom);
  const [, setLoyaltyDiscountCode] = useAtom(loyaltyDiscountCodeAtom);
  const { addDiscount } = useAddDiscount();
  const { track } = useTrackAnalytics();

  const shopId = formMessage?.shopId;

  /**
   * Apply the redeemed code to the cart, retrying while the freshly created Hey Pongo
   * discount propagates to the Shopify storefront. The storefront discount input is often
   * hidden, so a silently-dropped code would strand the shopper (their points are already
   * spent) — hence we retry rather than swallow a single failure.
   *
   * Runs inside the mutation so react-query owns the whole loading/error lifecycle; no
   * component state is written after the awaits (safe if the component unmounts mid-retry).
   *
   * @returns `true` once the code is applied, `false` if every attempt failed.
   * @example
   * const applied = await applyDiscountWithRetry('ZDM7479FN8');
   */
  const applyDiscountWithRetry = async (code: string): Promise<boolean> => {
    for (let attempt = 1; attempt <= DISCOUNT_APPLY_MAX_ATTEMPTS; attempt += 1) {
      try {
        await addDiscount({ code });
        return true;
      } catch {
        const isLastAttempt = attempt === DISCOUNT_APPLY_MAX_ATTEMPTS;

        if (isLastAttempt) {
          return false;
        }

        await new Promise((resolve) => {
          setTimeout(resolve, DISCOUNT_APPLY_RETRY_DELAY_MS);
        });
      }
    }

    return false;
  };

  const mutation = useMutation({
    mutationKey: [MUTATION_LOYALTY_REDEEM_KEY, shopId],
    mutationFn: async (input: RedeemInput) => {
      if (!shopId) {
        return { outcome: 'errored' as const, error: new Error('Shop ID is required'), applied: false };
      }

      const response = await gateway.post<Loyalty.LoyaltyCreateRedemptionResponseBody>(
        `/api/${encodeURIComponent(shopId)}/loyalty/redeem`,
        input,
      );
      const data = response.data;

      // Fold the apply (with its retries) into the mutation so isPending/isError cover the
      // whole operation — no shadow loading/error state, no setState after unmount.
      if (data.outcome !== 'created' || !data.discountCode) {
        return { ...data, applied: false };
      }

      const applied = await applyDiscountWithRetry(data.discountCode);
      return { ...data, applied };
    },
    onSuccess: (data) => {
      if (data.outcome !== 'created') {
        return;
      }

      // Points were consumed server-side regardless of the apply outcome — always refresh.
      queryClient.invalidateQueries({ queryKey: [LOYALTY_BALANCE_QUERY_KEY] });

      if (!data.discountCode) {
        return;
      }

      track(AnalyticsEventName.LOYALTY_REDEEM_SUCCEEDED, { discountCode: data.discountCode });
      setLoyaltyDiscountCode(data.discountCode);

      if (data.applied) {
        setRedeemed(true);
      } else {
        track(AnalyticsEventName.LOYALTY_REDEEM_DISCOUNT_AUTO_APPLY_FAILED, {
          discountCode: data.discountCode,
        });
      }
    },
    onError: () => {
      track(AnalyticsEventName.LOYALTY_REDEEM_FAILED);
    },
  });

  const result = mutation.data;
  const redeemFailed = mutation.isError || (result !== undefined && result.outcome !== 'created');
  const autoApplyFailed = result?.outcome === 'created' && result.applied === false;

  return {
    redeem: mutation.mutate,
    isLoading: mutation.isPending,
    isError: Boolean(redeemFailed || autoApplyFailed),
    isRedeemed: redeemed,
    result: mutation.data,
    error: mutation.error,
    reset: mutation.reset,
  };
};
